import 'tippy.js/dist/tippy.css'
import 'tippy.js/animations/shift-away.css'
import 'tippy.js/themes/light-border.css'

import {
  type ColumnFiltersState,
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import React, { useCallback, useEffect, useState } from 'react'
import tippy from 'tippy.js'

import { brokersEmployees } from 'rails_routes'
import { useWindowLocation } from 'utils/windowLocation'

import { type Employer } from './types'
import { useGetBrokersEmployers } from '../hooks/useGetBrokersEmployers'
import TanstackTable, { type PaginationMetadata } from '../reusable/table/TanstackTable'
import { TextFilter } from '../reusable/table/TextFilter'

type EmployerFilter = {
  name?: string;
}

const PAGE_SIZE = 20

const columnHelper = createColumnHelper<Employer>()

const filterableKeys = [
  'name',
]

function getInitialFilters (filters?: EmployerFilter) {
  const initialFilters: ColumnFiltersState = []
  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      initialFilters.push({
        id: key,
        value,
      })
    })
  }
  return initialFilters
}

const BrokersEmployersTableImpl = ({ pageParam, filterParams }: { pageParam?: number, filterParams?: EmployerFilter }) => {
  const { assign } = useWindowLocation()

  const [paginationMetadata, setPaginationMetadata] = useState<PaginationMetadata | undefined>()

  const initialFilters = getInitialFilters(filterParams)
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(initialFilters)
  const [pagination, setPagination] = useState({
    pageIndex: pageParam || 1,
    pageSize: PAGE_SIZE,
  })

  const { data: employerData, isLoading, isError } = useGetBrokersEmployers({ pagination, filters: columnFilters })

  useEffect(() => {
    if (employerData) {
      setPaginationMetadata(employerData.pagination)
    }
  }, [employerData?.pagination])

  const columns = [
    columnHelper.accessor('name', {
      header: 'Name',
      cell: ({ row }) => {
        return <div className='text-gray-900'>{row.getValue('name')}</div>
      },
    }),
    columnHelper.accessor('location', {
      header: 'Location',
    }),
    columnHelper.accessor('status', {
      header: 'Onboarding status',
      cell: ({ row }) => {
        let classes = ''
        let label = ''
        let statusTip = ''

        switch (row.getValue('status')) {
          case ('in_progress'):
            classes = 'bg-yellow-100 text-yellow-800'
            label = 'In progress'
            statusTip = 'Employer has not completed sign up'
            break
          case ('completed'):
            classes = 'bg-green-100 text-green-600'
            label = 'Complete'
            statusTip = 'Employer has completed sign up and is able to invite employees'
            break
          default:
            classes = 'bg-gray-100 text-gray-800'
            label = 'Unknown'
            statusTip = "This employer's current status is unknown. Please reach out to support@thatch.com"
        }

        return (
          <div
            onMouseEnter={(e) => handleStatusHover(e, statusTip)}
            className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ${classes}`}
          >
            {label}
          </div>
        )
      },
    }),
  ]

  const table = useReactTable({
    data: employerData?.data || [],
    columns,
    manualPagination: true,
    manualFiltering: true,
    getCoreRowModel: getCoreRowModel(),
    state: {
      columnFilters,
      pagination,
    },
  })

  const handleStatusHover = (event: any, statusTip: any) => {
    event.stopPropagation()
    tippy(event.target, {
      content: statusTip,
      theme: 'light-border',
      animation: 'shift-away',
      arrow: true,
      placement: 'bottom',
      trigger: 'mouseenter',
      delay: 500,
    })
  }

  // update the URL as pagination or column filters change
  useEffect(() => {
    const pageURL = new URL(window.location.href)
    // reset searchParams
    filterableKeys.forEach((filterKey) => {
      pageURL.searchParams.delete(`${filterKey}`)
    })
    // set searchParams and api query params with page index
    const pageIndex = pagination.pageIndex.toString()
    const queryParams: Record<string, any> = {
      'page[number]': pageIndex,
      'page[size]': PAGE_SIZE,
    }
    for (const filter of columnFilters) {
      queryParams[`filter[${filter.id}]`] = filter.value
      pageURL.searchParams.set(filter.id, filter.value as string)
    }

    // change the URL of the page without reloading the page
    window.history.pushState({}, '', pageURL.toString())
  }, [pagination.pageIndex, columnFilters])

  const handleSetColumnFilters = useCallback(
    (filters: ColumnFiltersState) => {
      setColumnFilters(filters)

      // reset the page index to 1 when filters are being applied
      if (pagination.pageIndex !== 1) {
        setPagination({ ...pagination, pageIndex: 1 })
      }
    },
    [pagination]
  )

  return (
    <>
      {/* Searchbar */}
      <div className='flex items-center justify-between w-full mb-4 gap-x-2'>
        <TextFilter<EmployerFilter>
          property='name'
          placeholder='Employer'
          columnFilters={columnFilters}
          setColumnFilters={handleSetColumnFilters}
        />
      </div>
      {/* Table */}
      <TanstackTable
        table={table}
        isLoading={isLoading}
        isError={isError}
        noDataMessage='No employers found'
        onRowClick={({ row }) => {
          const id = (row.original as { id: string }).id
          assign(brokersEmployees.index({ employer_id: id }))
        }}
        isThisRowClicklable={(_row) => true}
        paginationLabelSingular='record'
        paginationMetadata={paginationMetadata}
        onNextPageClick={() =>
          setPagination({ ...pagination, pageIndex: paginationMetadata?.next_page || 1 })}
        onPreviousPageClick={() =>
          setPagination({ ...pagination, pageIndex: paginationMetadata?.prev_page || 1 })}
      />
    </>
  )
}

const BrokersEmployersTable = ({ pageParam, filterParams }: { pageParam?: number, filterParams?: EmployerFilter }) => {
  return <BrokersEmployersTableImpl pageParam={pageParam} filterParams={filterParams} />
}

export default BrokersEmployersTable
